<template>
  <Teleport to="body">
    <Transition name="mobile-edit-tip-fade">
      <div
        v-if="modelValue"
        class="mobile-edit-tip-overlay"
        role="presentation"
        @click="handleClose"
      >
        <section
          class="mobile-edit-tip-sheet"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-edit-tip-title"
          @click.stop
        >
          <header class="mobile-edit-tip-header">
            <h2 id="mobile-edit-tip-title" class="mobile-edit-tip-title">内容编辑请使用电脑端</h2>
            <button
              class="mobile-edit-tip-collapse"
              type="button"
              aria-label="关闭提示"
              @click="handleClose"
            >
              <SvgIcon name="mobile-edit-collapse" :size="12" />
            </button>
          </header>

          <div class="mobile-edit-tip-body">
            <p class="mobile-edit-tip-message">更好的编辑体验请前往电脑端，移动端暂不支持</p>
            <div class="mobile-edit-tip-link" :title="editUrl">{{ editUrl }}</div>
            <button class="mobile-edit-tip-copy" type="button" @click="handleCopyLink">
              复制链接
            </button>
          </div>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  import { ElMessage } from 'element-plus';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';

  const props = defineProps<{
    modelValue: boolean;
    editUrl: string;
  }>();

  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
  }>();

  const handleClose = () => {
    emit('update:modelValue', false);
  };

  async function copyText(text: string): Promise<boolean> {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        // 继续尝试 textarea 回退方案
      }
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      return document.execCommand('copy');
    } catch {
      return false;
    } finally {
      document.body.removeChild(textarea);
    }
  }

  const handleCopyLink = async () => {
    if (!props.editUrl) {
      ElMessage.error('链接不可用，请稍后重试');
      return;
    }

    const success = await copyText(props.editUrl);
    if (success) {
      ElMessage.success('链接已复制');
      handleClose();
      return;
    }

    ElMessage.error('复制失败，请长按链接手动复制');
  };
</script>

<style scoped>
  .mobile-edit-tip-overlay {
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    align-items: flex-end;
    background: rgba(0, 0, 0, 0.3);
  }

  .mobile-edit-tip-sheet {
    width: 100%;
    padding: 16px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 16px 16px 0 0;
    background: #fff;
  }

  .mobile-edit-tip-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    min-height: 24px;
  }

  .mobile-edit-tip-title {
    margin: 0;
    color: #160211;
    font-family: 'ABeeZee', 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 24px;
  }

  .mobile-edit-tip-collapse {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 24px;
    padding: 0 16px;
    box-sizing: border-box;
    border: 1px solid #eff0f7;
    border-radius: 66px;
    background: #fff;
    color: #160211;
    cursor: pointer;
    flex-shrink: 0;
  }

  .mobile-edit-tip-collapse:active {
    background: #f9fafb;
  }

  .mobile-edit-tip-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .mobile-edit-tip-message {
    margin: 0;
    color: #999;
    font-family: 'ABeeZee', 'PingFang SC', 'Source Han Sans CN VF', 'Noto Sans SC', sans-serif;
    font-size: 13px;
    font-weight: 400;
    line-height: 24px;
  }

  .mobile-edit-tip-link {
    width: 100%;
    padding: 10px 12px;
    box-sizing: border-box;
    border-radius: 6px;
    background: #fbf9ff;
    color: #999;
    font-family: 'Microsoft YaHei', 'PingFang SC', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .mobile-edit-tip-copy {
    width: 100%;
    height: 40px;
    border: 0;
    border-radius: 6px;
    background: #6033e1;
    color: #fff;
    font-family: 'Source Han Sans CN VF', 'PingFang SC', 'Noto Sans SC', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    cursor: pointer;
  }

  .mobile-edit-tip-copy:active {
    background: #5128c7;
  }

  .mobile-edit-tip-copy:focus-visible,
  .mobile-edit-tip-collapse:focus-visible {
    outline: 2px solid rgba(96, 51, 225, 0.35);
    outline-offset: 2px;
  }

  .mobile-edit-tip-fade-enter-active,
  .mobile-edit-tip-fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .mobile-edit-tip-fade-enter-active .mobile-edit-tip-sheet,
  .mobile-edit-tip-fade-leave-active .mobile-edit-tip-sheet {
    transition: transform 0.2s ease;
  }

  .mobile-edit-tip-fade-enter-from,
  .mobile-edit-tip-fade-leave-to {
    opacity: 0;
  }

  .mobile-edit-tip-fade-enter-from .mobile-edit-tip-sheet,
  .mobile-edit-tip-fade-leave-to .mobile-edit-tip-sheet {
    transform: translateY(100%);
  }
</style>
